/**
 * UI chrome state — sheets, modals, theme.
 */
import { useLayersStore } from '@/stores/layers';
import { useToolsStore } from '@/stores/tools';
import { defineStore } from 'pinia';
import { nextTick, ref, watch } from 'vue';

export type PaywallContext = 'trace' | 'fill' | 'export' | 'pattern';
export type Theme = 'dark' | 'light';
/** Which edge the tool strip docks to in landscape (brief 11-layout2 §1) —
 *  Tessellation/Layers tab access always takes the opposite edge; the two
 *  swap together under this one setting, never independently. */
export type DominantSide = 'left' | 'right';

const THEME_KEY = 'esch_theme';
const ONBOARDED_KEY = 'esch_onboarded';
const DOMINANT_SIDE_KEY = 'esch_dominant_side';

function loadTheme(): Theme {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function loadDominantSide(): DominantSide {
  try {
    return localStorage.getItem(DOMINANT_SIDE_KEY) === 'left' ? 'left' : 'right';
  } catch {
    return 'right';
  }
}

function loadOnboarded(): boolean {
  try {
    return localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    return false;
  }
}

export const useUiStore = defineStore('ui', () => {
  const showPaywall = ref(false);
  const paywallContext = ref<PaywallContext>('pattern');
  const showGallery = ref(false);
  /** Export sheet (brief 01-export.md / 03-procreate.md) — mode picker + panels. */
  const showExport = ref(false);
  /** Render PSD (brief 03-procreate.md "Rendering"). */
  const showRenderPsd = ref(false);
  /** Set by openWithPsd.ts when a PSD is opened/shared into the app from the
   *  OS (native "Open with…"/share sheet); RenderPsdSheet consumes it and
   *  renders immediately. null the rest of the time. */
  const pendingPsdBuffer = ref<ArrayBuffer | null>(null);
  const theme = ref<Theme>(loadTheme());

  /** First-run welcome overlay — shown until the user has been onboarded once. */
  const hasOnboarded = ref(loadOnboarded());
  const showOnboarding = ref(!hasOnboarded.value);
  /** The always-available help sheet (the toolbar's "?" button). */
  const showHelp = ref(false);
  /** The settings sheet (top bar overflow menu — brief 11-layout2 §1). */
  const showSettings = ref(false);
  /** Landscape tool-strip/tab-access edge (brief 11-layout2 §1). */
  const dominantSide = ref<DominantSide>(loadDominantSide());
  watch(dominantSide, (side) => {
    try {
      localStorage.setItem(DOMINANT_SIDE_KEY, side);
    } catch {
      // persistence is best-effort
    }
  });

  // Dark/light is a different physical medium for drawing colour (brief
  // 10-colours: graphite/pencil vs. chalk/pastel, same hue) — keep the tools
  // store's `isDark` in step so `tools.colours` recomputes with the right
  // medium transform, and nudge a repaint since sketch/ink caches are keyed
  // on the resolved hex, not the theme itself.
  watch(
    theme,
    (t) => {
      document.documentElement.dataset.theme = t;
      try {
        localStorage.setItem(THEME_KEY, t);
      } catch {
        // theme persistence is best-effort
      }
      useToolsStore().isDark = t === 'dark';
      nextTick(() => useLayersStore().rerenderAllSketchLayers());
    },
    { immediate: true },
  );

  function toggleTheme(): void {
    theme.value = theme.value === 'dark' ? 'light' : 'dark';
  }

  function openPaywall(context: PaywallContext): void {
    paywallContext.value = context;
    showPaywall.value = true;
  }

  /** Dismiss the first-run overlay and remember it (best-effort). */
  function completeOnboarding(): void {
    showOnboarding.value = false;
    hasOnboarded.value = true;
    try {
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      // onboarding persistence is best-effort
    }
  }

  /** Re-open the welcome tour from the help sheet. */
  function replayOnboarding(): void {
    showHelp.value = false;
    showOnboarding.value = true;
  }

  function openHelp(): void {
    showHelp.value = true;
  }

  function toggleDominantSide(): void {
    dominantSide.value = dominantSide.value === 'right' ? 'left' : 'right';
  }

  return {
    showPaywall,
    paywallContext,
    showGallery,
    showExport,
    showRenderPsd,
    pendingPsdBuffer,
    theme,
    dominantSide,
    toggleDominantSide,
    showSettings,
    hasOnboarded,
    showOnboarding,
    showHelp,
    toggleTheme,
    openPaywall,
    completeOnboarding,
    replayOnboarding,
    openHelp,
  };
});
